
import { createContext, ReactNode, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiV2 } from '../services/api';
import LocalStorageService from '../services/storage';

const localStorageService = LocalStorageService();

interface CostCategory {
  id_category: string;
  costs_categories_name: string;
  type: string;
  economic_behavior: string;
  financial_behavior: string;
  economic_behavior_description?: string;
  financial_behavior_description?: string;
  isactive?: boolean;
}

type CostCategoryInput = Omit<CostCategory, 'id_category'>

interface CostCategoriesProvidersProps {
  children: ReactNode;
}

interface ResponseProps {
  success: boolean;
  message: string;
}

interface CostCategoriesContextData {
  createCostCategory: (costCategory: CostCategoryInput) => Promise<ResponseProps>;
  updateCostCategory: (costCategory: CostCategory) => Promise<ResponseProps>;
  updateOnDelete: (id: number) => Promise<ResponseProps>;
}

interface UseCostCategoriesOptions {
  loadCostCategories?: boolean;
}

interface UseCostCategoriesData extends CostCategoriesContextData {
  costCategories: CostCategory[];
}

const CostCategoriesContext = createContext<CostCategoriesContextData>(
  {} as CostCategoriesContextData
);

export const economicBehaviorOptions = [
  {
    id: 'VARIABLE_TRIP',
    label: 'Custo variavel de viagem'
  },
  {
    id: 'BOAT_FIXED',
    label: 'Custo fixo de barco'
  },
  {
    id: 'COMPANY_OVERHEAD',
    label: 'Despesa da empresa'
  }
];

export const financialBehaviorOptions = [
  {
    id: 'VENDOR',
    label: 'Fornecedor'
  },
  {
    id: 'PAYROLL',
    label: 'Folha de pagamento'
  },
  {
    id: 'TAXES',
    label: 'Impostos'
  },
  {
    id: 'NON_OPERATIONAL',
    label: 'Não operacional'
  }
];

const costCategoriesQueryKey = ['cost-categories'];
const emptyCostCategories: CostCategory[] = [];

function hasToken() {
  return Boolean(localStorageService.getIdToken());
}

function normalizeCostCategory(costCategory: CostCategory) {
  return {
    ...costCategory,
    economic_behavior_description: economicBehaviorOptions.find(option => option.id === costCategory.economic_behavior)?.label || costCategory.economic_behavior,
    financial_behavior_description: financialBehaviorOptions.find(option => option.id === costCategory.financial_behavior)?.label || costCategory.financial_behavior
  };
}

async function fetchCostCategories() {
  const response = await apiV2.get('cost-categories');
  return Array.isArray(response?.data) ? response.data.map(normalizeCostCategory) : [];
}

export function CostCategoriesProvider({ children }: CostCategoriesProvidersProps) {
  const queryClient = useQueryClient();

  function invalidateCostCategories() {
    queryClient.invalidateQueries({ queryKey: costCategoriesQueryKey });
  }

  async function updateOnDelete(id: number) {
    const response = await apiV2.delete(`/cost-categories/${id}`);
    if (response.status === 200) {
      invalidateCostCategories();
      return {
        success: true,
        message: "Categoria removida com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao remover categoria."
    };
  }

  async function createCostCategory(costCategoryInput: CostCategoryInput) {
    const response = await apiV2.post('/cost-categories', costCategoryInput);
    if (response.status === 201) {
      invalidateCostCategories();

      return {
        success: true,
        message: "Categoria adicionada com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao adicionar categoria."
    };
  }

  async function updateCostCategory(costCategory: CostCategory) {
    const response = await apiV2.put(`/cost-categories/${costCategory.id_category}`, costCategory);
    if (response.status === 204) {
      invalidateCostCategories();

      return {
        success: true,
        message: "Categoria editada com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao editar categoria."
    };
  }

  return (
    <CostCategoriesContext.Provider value={{ createCostCategory, updateCostCategory, updateOnDelete }}>
      {children}
    </CostCategoriesContext.Provider>
  );
}

export function UseCostCategories(options: UseCostCategoriesOptions = {}): UseCostCategoriesData {
  const context = useContext(CostCategoriesContext);
  const { loadCostCategories = false } = options;

  const costCategoriesQuery = useQuery({
    queryKey: costCategoriesQueryKey,
    queryFn: fetchCostCategories,
    enabled: loadCostCategories && hasToken(),
    staleTime: 5 * 60 * 1000
  });

  return {
    ...context,
    costCategories: costCategoriesQuery.data || emptyCostCategories
  };
}
